import { Epic, ofType } from "redux-observable";
import { ApiUpload } from "../../service/ApiUpload";
import { RootState } from "../../store";
import * as venueActions from "./venueActions";
import * as authSelectors from "../auth/authSelectors";
import { ServiceContainer } from "../../serviceContainer";
import { AjaxError } from "rxjs/ajax";
import { OrganizationKeyName } from "../../model/Organization";
import { from, of, Observable, merge } from "rxjs";
import {
  catchError,
  exhaustMap,
  last,
  map,
  mergeMap,
  tap,
  toArray,
} from "rxjs/operators";

const fileUpload = (
  apiUploadService: ApiUpload,
  organization: OrganizationKeyName,
  state: RootState,
  files: File[],
  venueId: number
) => {
  const accessToken = authSelectors.accessToken(state);

  return from(files).pipe(
    mergeMap((file) =>
      apiUploadService
        .uploadVenueImage(accessToken, organization, file, venueId)
        .pipe(
          catchError(() => of(["error", file.name])),
          map((x) =>
            x === false ? ["error", file.name] : ["success", file.name]
          )
        )
    ),
    toArray()
  );
};

const venueLogoUpload = (
  apiUploadService: ApiUpload,
  organization: OrganizationKeyName,
  state: RootState,
  file: File,
  venueId: number
) => {
  const accessToken = authSelectors.accessToken(state);

  return from(
    apiUploadService
      .uploadVenueLogoImage(accessToken, organization, file, venueId)
      .pipe(
        catchError(() => of(["error", file.name])),
        map((x) =>
          x === false ? ["error", file.name] : ["success", file.name]
        ),
        toArray()
      )
  );
};

export const getVenueForEditEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(venueActions.GET_VENUE_FOR_EDIT),
    exhaustMap((action: venueActions.GetVenueForEditAction) => {
      const state = state$.value;

      return services.apiVenue
        .get(
          authSelectors.accessToken(state),
          authSelectors.selectedOrganizationKeyName(state),
          action.id
        )
        .pipe(
          map((x) =>
            venueActions.getVenueForEditSuccessAction(
              action.organization,
              x.venue
            )
          ),
          catchError(() => {
            return of(
              venueActions.getVenueForEditFailAction(action.organization)
            );
          })
        );
    })
  );

export const updateVenueEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(venueActions.UPDATE_VENUE),
    exhaustMap((action: venueActions.UpdateVenueAction) => {
      const state = state$.value;
      return services.apiVenue
        .update(
          authSelectors.accessToken(state),
          action.organization,
          action.id,
          action.data
        )
        .pipe(
          mergeMap(() => {
            let obs: Observable<any>[] = [];
            obs.push(
              fileUpload(
                services.apiUpload,
                action.organization,
                state,
                action.files,
                action.id
              )
            );

            if (action.logo) {
              obs.push(
                venueLogoUpload(
                  services.apiUpload,
                  action.organization,
                  state,
                  action.logo,
                  action.id
                )
              );
            }
            return merge(...obs);
          }),
          tap((files) => {
            if (files) {
              const errors = files.filter((x: any) => x[0] === "error");
              errors.forEach((x: any) =>
                services.toastr.error(
                  "Error while uploading file",
                  `File ${x[1]} was not uploaded`
                )
              );
            }
          }),
          last(),
          tap(() =>
            services.toastr.success(
              "Update venue",
              "Venue was updated sucessfully"
            )
          ),
          map(() => venueActions.updateVenueSuccessAction(action.organization)),
          catchError((x: AjaxError) => {
            if (
              x.status === 400 &&
              x.xhr.response.errors &&
              x.xhr.response.errors.length
            ) {
              services.toastr.warning(
                "Warning",
                `Some values in the form are invalid`
              );
              return of(
                venueActions.updateVenueFailAction(
                  action.organization,
                  x.xhr.response.errors
                )
              );
            } else {
              services.toastr.error(
                "Update venue error",
                "There was error while updating venue"
              );
              return of(
                venueActions.updateVenueFailAction(action.organization)
              );
            }
          })
        );
    })
  );

export const insertVenueEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(venueActions.INSERT_VENUE),
    exhaustMap((action: venueActions.InsertVenueAction) => {
      const state = state$.value;
      return services.apiVenue
        .insert(
          authSelectors.accessToken(state),
          action.organization,
          action.data
        )
        .pipe(
          mergeMap((venueId) => {
            let obs: Observable<any>[] = [];
            obs.push(
              fileUpload(
                services.apiUpload,
                action.organization,
                state,
                action.files,
                venueId
              )
            );

            if (action.logo) {
              obs.push(
                venueLogoUpload(
                  services.apiUpload,
                  action.organization,
                  state,
                  action.logo,
                  venueId
                )
              );
            }

            return merge(...obs);
          }),
          tap((files) => {
            const isFilesToUploadArray = files && Array.isArray(files);
            if (isFilesToUploadArray) {
              const errors = files.filter((x: any) => x[0] === "error");
              errors.forEach((x: any) =>
                services.toastr.error(
                  "Error while uploading file",
                  `File ${x[1]} was not uploaded`
                )
              );
            }
          }),
          last(),
          tap(() =>
            services.toastr.success(
              "Update venue",
              "Venue was added successfully"
            )
          ),
          map(() => venueActions.insertVenueSuccessAction(action.organization)),
          catchError((x: AjaxError) => {
            if (
              x.status === 400 &&
              x.xhr.response.errors &&
              x.xhr.response.errors.length
            ) {
              services.toastr.warning(
                "Warning",
                `Some values in the form are invalid`
              );
              return of(
                venueActions.insertVenueFailAction(
                  action.organization,
                  x.xhr.response.errors
                )
              );
            } else {
              console.error(x);
              services.toastr.error(
                "New venue error",
                `There was error while adding venue`
              );
              return of(
                venueActions.insertVenueFailAction(action.organization)
              );
            }
          })
        );
    })
  );

export const deleteVenueImageEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(venueActions.DELETE_VENUE_IMAGE),
    mergeMap((action: venueActions.DeleteVenueImageAction) => {
      const state = state$.value;
      const orgKeyName = authSelectors.selectedOrganizationKeyName(state);

      return services.apiUpload
        .deleteVenueImage(
          authSelectors.accessToken(state),
          action.organization,
          action.id,
          action.imageUrl
        )
        .pipe(
          tap(() =>
            services.toastr.success("Success", "Venue image was deleted")
          ),
          map(() =>
            venueActions.deleteVenueImageSuccessAction(
              orgKeyName,
              action.id,
              action.imageUrl
            )
          ),
          catchError((x: AjaxError) => {
            services.toastr.error("Error", `Venue image was not deleted`);
            return of(
              venueActions.deleteVenueImageFailAction(
                action.organization,
                action.id,
                action.imageUrl
              )
            );
          })
        );
    })
  );
